import { createContext, useContext, ReactNode } from 'react';
import axios, { AxiosInstance } from 'axios';

interface ApiContextType {
  api: AxiosInstance;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // Aumentado a 120 segundos para operaciones lentas
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para manejar FormData correctamente
api.interceptors.request.use((config) => {
  // Si el data es FormData, eliminar el Content-Type para que el navegador lo establezca con el boundary
  if (config.data instanceof FormData) {
    if (config.headers) {
      delete config.headers['Content-Type'];
    }
  }
  return config;
});

// Interceptor para manejar errores globalmente y reintentos automáticos
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Log más detallado del error (solo en desarrollo)
    if (error.response) {
      // El servidor respondió con un código de estado fuera del rango 2xx
      if (import.meta.env.DEV) {
        console.error('API Error Response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          url: error.config?.url,
          method: error.config?.method,
        });
      }

      // Reintentar automáticamente en errores 5xx (errores del servidor)
      if (error.response.status >= 500 && error.response.status < 600) {
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
          
          // Máximo 3 reintentos
          if (originalRequest._retryCount <= 3) {
            // Backoff exponencial: 1s, 2s, 4s
            const delay = Math.pow(2, originalRequest._retryCount - 1) * 1000;
            if (import.meta.env.DEV) {
              console.log(`🔄 Reintentando petición (intento ${originalRequest._retryCount}/3) después de ${delay}ms...`);
            }
            
            await new Promise((resolve) => setTimeout(resolve, delay));
            return api(originalRequest);
          }
        }
      }

      // Manejar errores 401 (no autorizado) - redirigir a login
      if (error.response.status === 401) {
        // Solo redirigir si no estamos ya en la página de login
        if (window.location.pathname !== '/login') {
          if (import.meta.env.DEV) {
            console.warn('⚠️ Sesión expirada, redirigiendo a login...');
          }
          // Limpiar datos de usuario si existen (usando la misma clave que AuthContext)
          localStorage.removeItem('seekers_stock_user');
          // Limpiar header de autorización
          delete api.defaults.headers.common['Authorization'];
          window.location.href = '/login';
        }
      }
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta (error de red)
      if (import.meta.env.DEV) {
        console.error('API Error Request:', {
          message: 'No se recibió respuesta del servidor',
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
        });
      }

      // Reintentar automáticamente en errores de red
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
        
        // Máximo 3 reintentos
        if (originalRequest._retryCount <= 3) {
          // Backoff exponencial: 1s, 2s, 4s
          const delay = Math.pow(2, originalRequest._retryCount - 1) * 1000;
          if (import.meta.env.DEV) {
            console.log(`🔄 Reintentando petición por error de red (intento ${originalRequest._retryCount}/3) después de ${delay}ms...`);
          }
          
          await new Promise((resolve) => setTimeout(resolve, delay));
          return api(originalRequest);
        }
      }
    } else {
      // Algo pasó al configurar la petición
      if (import.meta.env.DEV) {
        console.error('API Error Config:', error.message);
      }
    }
    
    return Promise.reject(error);
  }
);

export function ApiProvider({ children }: { children: ReactNode }) {
  return (
    <ApiContext.Provider value={{ api }}>
      {children}
    </ApiContext.Provider>
  );
}

export function useApi() {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
}

